import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { SignupForm } from '../components/signup/SignupForm';
import { VerificationForm } from '../components/signup/VerificationForm';
import { signupError, useSignupStatus } from '../components/signup/SignupStatus';
import './signup.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/signup`;
const empty = { business_name: '', business_type: 'cafe', owner_name: '', email: '', phone: '', password: '', invite_code: '' };
// A friend's link (/signup?ref=CODE) pre-fills the invite code.
const refFromUrl = () => {
  try { return (new URLSearchParams(window.location.search).get('ref') || '').trim().toUpperCase().slice(0, 32); } catch { return ''; }
};

export default function SignupPage() {
  const [enabled, setEnabled] = useSignupStatus();
  const [values, setValues] = useState(() => ({ ...empty, invite_code: refFromUrl() }));
  const [stage, setStage] = useState('start');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [seconds, setSeconds] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setTimeout(() => setSeconds(value => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  const submit = async (event, action) => {
    event?.preventDefault();
    setBusy(true); setError(''); setNotice('');
    try {
      if (action === 'start') {
        const utf8Length = unescape(encodeURIComponent(values.password)).length;
        if (utf8Length > 72) throw new Error('password-too-long');
        const email = values.email.trim().toLowerCase();
        const { data } = await axios.post(`${API}/start`, { ...values, email });
        setValues(previous => ({ ...previous, email, password: '' }));
        setStage('verify'); setCode(''); setSeconds(60); setNotice(data.message);
      } else if (action === 'resend-code') {
        const { data } = await axios.post(`${API}/resend-code`, { email: values.email });
        setCode(''); setSeconds(60); setNotice(data.message);
      } else {
        const { data } = await axios.post(`${API}/verify`, { email: values.email, code });
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('admin_role', data.role);
        localStorage.setItem('admin_club_id', data.club_id);
        localStorage.setItem('admin_username', data.username);
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (failure) {
      if (failure.response?.status === 404) setEnabled(false);
      setError(failure.message === 'password-too-long' ? 'Нууц үг UTF-8-аар 72 байтаас ихгүй байна.' : signupError(failure));
    } finally { setBusy(false); }
  };

  return <main className="signup-page" data-testid="signup-page">
    <header className="signup-header">
      <Link to="/" data-testid="signup-brand-link" className="signup-brand">RENTOS</Link>
      <Link to="/" data-testid="signup-login-link" className="signup-back"><ArrowLeft size={16} />Нэвтрэх</Link>
    </header>
    <section className="signup-content">
      {enabled === null ? <p role="status" data-testid="signup-status-loading">Уншиж байна…</p> : enabled === false ? <>
        <h1 data-testid="signup-closed-heading">Бүртгэл хаалттай</h1>
        <p data-testid="signup-closed-message" className="signup-subtitle">Шинэ бизнесийн бүртгэлийг түр хаасан байна.</p>
        <Link to="/" data-testid="signup-closed-login-link" className="signup-back">Нэвтрэх хуудас руу <ArrowLeft size={16} /></Link>
      </> : <>
        <p data-testid="signup-trial-badge" className="signup-trial"><ShieldCheck size={16} />{values.invite_code ? 'Урилгын кодоор 60 хоног үнэгүй' : '30 хоног үнэгүй'}</p>
        <h1 data-testid="signup-heading">{stage === 'start' ? 'Бизнесээ бүртгүүлэх' : 'Email баталгаажуулах'}</h1>
        <p data-testid="signup-step" className="signup-subtitle">{stage === 'start' ? '01 / Бизнесийн мэдээлэл' : '02 / Баталгаажуулалт'}</p>
        {error && <div data-testid="signup-error" role="alert" className="signup-error">{error}</div>}
        {notice && <div data-testid="signup-notice" role="status" className="signup-notice">{notice}</div>}
        {stage === 'start' ? <SignupForm values={values} setValues={setValues} busy={busy}
          onSubmit={event => submit(event, 'start')} /> : <VerificationForm email={values.email} code={code}
          setCode={setCode} busy={busy} seconds={seconds} onSubmit={event => submit(event, 'verify')}
          onResend={() => submit(null, 'resend-code')} onBack={() => { setStage('start'); setCode(''); setError(''); setNotice(''); }} />}
      </>}
    </section>
  </main>;
}